/**
 * Simple toast notification utility
 * Shows temporary toast messages for user feedback
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  duration?: number; // milliseconds, default 5000
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const DEFAULT_DURATION = 5000;
const DEFAULT_POSITION = 'top-right';

export function showToast(message: string, type: ToastType = 'info', options: ToastOptions = {}) {
  const { duration = DEFAULT_DURATION, position = DEFAULT_POSITION } = options;
  
  // Create toast element
  const toast = document.createElement('div');
  
  // Style based on type
  const styles = {
    success: {
      background: '#10B981',
      color: 'white',
      borderLeft: '4px solid #059669',
      icon: '✅'
    },
    error: {
      background: '#EF4444',
      color: 'white', 
      borderLeft: '4px solid #DC2626',
      icon: '❌'
    },
    warning: {
      background: '#F59E0B',
      color: 'white',
      borderLeft: '4px solid #D97706', 
      icon: '⚠️'
    },
    info: {
      background: '#3B82F6',
      color: 'white',
      borderLeft: '4px solid #2563EB',
      icon: 'ℹ️'
    }
  };
  
  const style = styles[type];
  
  // Position styles
  const positions = {
    'top-right': { top: '20px', right: '20px' },
    'top-left': { top: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' }
  };
  
  const positionStyle = positions[position];
  
  toast.style.cssText = `
    position: fixed;
    ${Object.entries(positionStyle).map(([key, value]) => `${key}: ${value}`).join('; ')};
    background: ${style.background};
    color: ${style.color};
    padding: 16px;
    border-radius: 8px;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    max-width: min(400px, calc(100vw - 40px));
    min-width: min(280px, calc(100vw - 40px));
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-left: ${style.borderLeft};
    transform: translateX(${position.includes('right') ? '100%' : '-100%'});
    transition: transform 0.3s ease-in-out;
    cursor: pointer;
  `;
  
  toast.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px;">
      <span style="font-size: 16px;">${style.icon}</span>
      <span style="flex: 1;">${message}</span>
      <span style="opacity: 0.7; font-size: 12px; margin-left: 8px;">×</span>
    </div>
  `;
  
  // Add to DOM
  document.body.appendChild(toast);
  
  // Animate in
  setTimeout(() => {
    toast.style.transform = 'translateX(0)';
  }, 100);
  
  // Auto-remove after duration
  const removeToast = () => {
    toast.style.transform = `translateX(${position.includes('right') ? '100%' : '-100%'})`;
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  };
  
  const timeoutId = setTimeout(removeToast, duration);
  
  // Click to dismiss
  toast.addEventListener('click', () => {
    clearTimeout(timeoutId);
    removeToast();
  });
}

// Convenience methods
export const toast = {
  success: (message: string, options?: ToastOptions) => showToast(message, 'success', options),
  error: (message: string, options?: ToastOptions) => showToast(message, 'error', options),
  warning: (message: string, options?: ToastOptions) => showToast(message, 'warning', options),
  info: (message: string, options?: ToastOptions) => showToast(message, 'info', options)
};
